<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('external_coach_billing.title') }}
        </h6>
      </div>

      <div class="element-box">
        <Menu :countUnreadMessageForCoordinator="countUnreadMessageForCoordinator"/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :coachDropdown="coachDropdown"
            @search="getRejectedCoachBills"/>

        <PerPage
            class="mt-3"
            :values="[10, 25, 50, 100]"
            @update:perPage="getRejectedCoachBills"/>


        <EmptyState
            v-if="!coachBills.data?.length"
            icon="fa-file-invoice"
            title="Keine Rechnungen gefunden"
            subtitle="Es wurden keine Rechnungen gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
<!--              <th style="width: 3%;"></th>-->
              <th>Rechnungs-Nr.</th>
              <th style="width: 20%;">Coach</th>
              <th>Kreditor</th>
              <th>Rechnungsdatum</th>
              <th class="text-end"># Sitzungen</th>
              <th class="text-end">Rechnungsbetrag</th>
              <th class="text-start"><span class="ps-5">Status</span></th>
              <th style="width: 5%;"></th>
              <th style="width: 5%;"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(coachBill) in coachBills.data" :key="coachBill.id">
<!--              <td class="text-center">-->
<!--                <input type="checkbox" v-model="selectedBillIds" :value="coachBill.id"/>-->
<!--              </td>-->
              <td>GS {{ coachBill?.billNumber }}</td>
              <td>{{ coachBill?.coach?.firstName }} {{ coachBill?.coach?.lastName }}</td>
              <td>{{ coachBill?.coach?.creditor}}</td>
              <td>{{ coachBill.createdAt }}</td>
              <td class="text-end">{{ coachBill.totalUnits }}</td>
              <td class="text-end">{{ coachBill.sum }}</td>
              <td class="text-start" style="min-width: 160px">
                <div class="ps-3">
                  <div v-if="coachBill.status === 'billed' || coachBill.status === 'approved'"
                       class="status-pill green p-2 me-2"
                       :data-title="`coachBill.statusTranslation`"
                       data-bs-toggle="tooltip"
                       data-original-title=""
                       title="">
                  </div>
                  <div v-else-if="coachBill.status === 'pending'"
                       class="status-pill yellow p-2 me-2"
                       :data-title="`coachBill.statusTranslation`"
                       data-bs-toggle="tooltip"
                       data-original-title=""
                       title="">
                  </div>
                  <div v-else
                       class="status-pill red p-2 me-2"
                       :data-title="`coachBill.statusTranslation`"
                       data-bs-toggle="tooltip"
                       data-original-title=""
                       title="">
                  </div>
                  {{ coachBill.statusTranslation }}
                </div>
              </td>
              <td class="text-end">
                <div v-if="coachBill.status === 'rejected' || coachBill.status === 'answered'" style="padding-right: 15px;">
                  <router-link :to="{ name: 'coachBillingCoordinatorBillChat', params: { id: coachBill.id } }">
                    <i style="font-size: 26px; color: #43ad4e;" class="os-icon os-icon-mail-07"></i>
                    <span v-if="coachBill.messageForCoach"
                          style="
                              position: absolute;
                              font-size: 8px;
                              padding: 3px 8px;
                              background-color: #DC3545FF;
                              color: white;
                              border-radius: 11px;">!</span>
                  </router-link>
                </div>
              </td>
              <td style="min-width: 130px;">
                <PrintButton :coachBill="coachBill" icon/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <Pagination
            v-if="coachBills?.pagination?.total_pages > 1"
            :totalPages="coachBills?.pagination?.total_pages"
            :currentPage="coachBills?.pagination?.current_page"
            :totalItems="coachBills?.pagination?.total_items"
            :perPage="coachBills?.pagination?.items_per_page"
            @pagechanged="getRejectedCoachBills"/>

      </div>
    </div>

  </PageTemplate>
</template>


<script setup>
import coachBillingService from '@/api/CoachBilling';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import { getExternalCoachForCoordinatorDropdown } from '@/shared/dropdowns';
import PrintButton from '@/views/App/CoachBilling/Accountant/Existing/PrintButton.vue';
import Menu from '@/views/App/CoachBilling/Coordinator/Menu.vue';
import Search from '@/views/App/CoachBilling/Coordinator/RejectedBills/Search.vue';
import { onMounted, ref } from 'vue';

const coachBills                  = ref([]);
const coachDropdown               = ref([]);
const selectedBillIds             = ref([]);
const isSearching                 = ref(false);
const isLoaded                    = ref(false);
const countUnreadMessageForCoordinator  = ref(0);

const filters = ref({
  coachId: '',
  billNumber: '',
  withMessage: false,
});

async function getRejectedCoachBills(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachBills.value?.pagination?.items_per_page;

  try {
    const { data } = await coachBillingService.coordinatorGetRejectedBills({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachBills.value  = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function countUnreadMessages() {
  try {
    const { data } = await coachBillingService.coordinatorCountUnreadMessages();

    countUnreadMessageForCoordinator.value = data.data.count;
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getRejectedCoachBills(),
    getExternalCoachForCoordinatorDropdown(coachDropdown),
    countUnreadMessages(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>